/**
 * Modal Global de Ações
 * 
 * Modal que permite escolher entre:
 * - Gerenciar Sessões Ativas
 * - Enviar Mensagem/Email para usuário
 * 
 * @author Equipe GestaoeCRM
 * @date 06/11/2025
 */

import React, { useState } from 'react';
import { X, Activity, MessageSquare, Clock } from 'lucide-react';
import MultiSessionManager from './MultiSessionManager';
import SendMessageModal from './SendMessageModal';

interface GlobalActionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ActionType = 'menu' | 'sessions' | 'message';

/**
 * Componente GlobalActionModal
 * 
 * Modal responsivo que permite escolher entre diferentes ações rápidas
 */
const GlobalActionModal: React.FC<GlobalActionModalProps> = ({ isOpen, onClose }) => {
  const [currentAction, setCurrentAction] = useState<ActionType>('menu');

  // Se não estiver aberto, não renderizar
  if (!isOpen) {
    return null;
  }

  // Handler para voltar ao menu principal
  const handleBackToMenu = () => {
    setCurrentAction('menu');
  };

  // Handler para fechar completamente
  const handleClose = () => {
    setCurrentAction('menu');
    onClose();
  };

  // Handler para fechar ao clicar fora do modal
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Fechar apenas se o click foi diretamente no overlay (não no conteúdo)
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Renderizar conteúdo baseado na ação atual
  const renderContent = () => {
    switch (currentAction) {
      case 'sessions':
        return (
          <MultiSessionManager
            isOpen={true}
            onClose={handleBackToMenu}
            showOverlay={false}
            onFocusSession={(osId: string, sessionId: string) => {
              // Fechar modal ao focar em uma sessão
              handleClose();
              // Navegar para a OS (opcional - pode ser implementado)
              // window.location.href = `/os/${osId}`;
            }}
          />
        );

      case 'message':
        return (
          <SendMessageModal
            isOpen={true}
            onClose={handleBackToMenu}
            onSuccess={() => {
              // Fechar modal após enviar mensagem
              handleClose();
            }}
          />
        );

      default:
        return (
          <div 
            className="bg-background-glass backdrop-blur-glass border border-border-glass rounded-button shadow-glass max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border-glass/30">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-button border border-primary/30">
                  <Activity className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text-primary">
                    Ações Rápidas
                  </h2>
                  <p className="text-sm text-text-muted mt-1">
                    Escolha uma ação para continuar
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-background-glass-hover rounded-button transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5 text-text-muted hover:text-text-primary" />
              </button>
            </div>

            {/* Opções de Ação */}
            <div className="p-6 space-y-3">
              {/* Opção: Sessões Ativas */}
              <button
                onClick={() => setCurrentAction('sessions')}
                className="w-full flex items-center space-x-4 p-4 bg-background-glass-hover hover:bg-background-glass border border-border-glass rounded-button transition-all duration-300 hover:border-secondary/50 hover:shadow-lg group"
              >
                <div className="p-3 bg-green-500/20 rounded-button border border-green-500/30 group-hover:bg-green-500/30 transition-colors">
                  <Clock className="w-6 h-6 text-green-400" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-base font-semibold text-text-primary group-hover:text-secondary transition-colors">
                    Gerenciar Sessões Ativas
                  </h3>
                  <p className="text-sm text-text-muted mt-1">
                    Visualize e gerencie todas as suas sessões de trabalho ativas
                  </p>
                </div>
              </button>

              {/* Opção: Enviar Mensagem */}
              <button
                onClick={() => setCurrentAction('message')}
                className="w-full flex items-center space-x-4 p-4 bg-background-glass-hover hover:bg-background-glass border border-border-glass rounded-button transition-all duration-300 hover:border-secondary/50 hover:shadow-lg group"
              >
                <div className="p-3 bg-blue-500/20 rounded-button border border-blue-500/30 group-hover:bg-blue-500/30 transition-colors">
                  <MessageSquare className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-base font-semibold text-text-primary group-hover:text-secondary transition-colors">
                    Enviar Mensagem/Email
                  </h3>
                  <p className="text-sm text-text-muted mt-1">
                    Envie uma mensagem ou email para outro usuário do sistema
                  </p>
                </div>
              </button>
            </div>

            {/* Footer */}
            <div className="p-4 bg-background-glass-hover border-t border-border-glass/30 rounded-b-button">
              <button
                onClick={handleClose}
                className="w-full px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      {renderContent()}
    </div>
  );
};

export default GlobalActionModal;

